import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './hero-inclusions.api';

import type {
  CreateHeroInclusionRequest,
  UpdateHeroInclusionRequest,
  HeroInclusionListQuery,
} from '@utazzo/shared';

const QK = {
  list: (query: HeroInclusionListQuery) => ['hero-inclusions', 'list', query] as const,
  detail: (id: string) => ['hero-inclusions', 'detail', id] as const,
};

export const useHeroInclusionsList = (query: HeroInclusionListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.listHeroInclusions(query),
    staleTime: 30_000,
  });

export const useCreateHeroInclusion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateHeroInclusionRequest) => api.createHeroInclusion(payload),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['hero-inclusions', 'list'] });
      toast.success(`Added ${t.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add inclusion'),
  });
};

export const useUpdateHeroInclusion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateHeroInclusionRequest }) =>
      api.updateHeroInclusion(id, payload),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['hero-inclusions', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(t.id) });
      toast.success('Inclusion updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update inclusion'),
  });
};

export const useDeleteHeroInclusion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteHeroInclusion(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hero-inclusions', 'list'] });
      toast.success('Inclusion deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete inclusion'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
